import Grid from "@mui/material/Grid2";
import { Stack, Typography } from "@mui/material";
import { EyeIcon } from "../images/EyeIcon";
import { Link, useParams } from "react-router-dom";
import { AppRoutes } from "../../types/enums";
import { Penetration } from "../../types";
import { FlagComponent } from "../ui/FlagComponent";
import { EllipsisTooltip } from "../ui/CustomTooltip";
import { mockPenetrationRate } from "../../types/genereal";
import { indicatorMapper, RateIndicatorIcon } from "../ui/RateIndicatorIcon";
import React from "react";

const ItemList = ({
  id,
  index,
  name,
  industry,
  region,
  region_code,
  engagement,
  discovery,
}: Penetration) => {
  const { campaign_id } = useParams();
  return (
    <Grid
      container
      sx={{
        alignItems: "center",
        border: "1px solid #E0E0E0",
        padding: "14px 16px",
        borderRadius: "8px",
      }}
    >
      <Grid size={0.5}>
        <Stack direction="row" gap="4px" alignItems="center">
          <Typography
            variant="body1"
            sx={{
              color: "#1F2932",
              minWidth: "10px",
            }}
          >
            {index}
          </Typography>
        </Stack>
      </Grid>
      <Grid size={2.12}>
        <Link
          onClick={(e) => name === "--" && e.preventDefault()}
          to={`${AppRoutes.ACCOUNT}/${campaign_id}/${id}`}
          style={{ textDecoration: "none" }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#1F2932",
              paddingRight: "16px",
            }}
          >
            {name === null ? "--" : name}
          </Typography>
        </Link>
      </Grid>

      <Grid size={2.62}>
        <Typography
          variant="body1"
          sx={{
            color: "#1F2932",
            paddingRight: "16px",
          }}
        >
          {industry === null ? "--" : industry}
        </Typography>
      </Grid>
      <Grid size={2.01}>
        <Stack direction="row" gap="8px" alignItems="center">
          {region_code && region_code !== "--" && <FlagComponent country={region_code} />}
          <Typography
            variant="body1"
            sx={{
              color: "#1F2932",
              paddingRight: "16px",
            }}
          >
            {region === null ? "--" : region}
          </Typography>
        </Stack>
      </Grid>
      <Grid size={1.6}>
        <Stack
          alignItems="center"
          flexDirection="row"
          gap="8px"
          justifyContent="flex-end"
        >
          <RateIndicatorIcon
            indicatorRate={discovery.indicator}
            indicatorChange={discovery.change}
            indicatorChangeAt={discovery.changed_at}
          />
          <Typography
            variant="body1"
            sx={{
              color: "#1F2932",
              ...(discovery.indicator && {
                color: indicatorMapper[discovery.indicator].color,
              }),
            }}
          >
            {discovery.score === null ? "--" : `${discovery.score}%`}
          </Typography>
        </Stack>
      </Grid>
      <Grid size={1.85}>
        <Stack
          alignItems="center"
          flexDirection="row"
          gap="8px"
          justifyContent="flex-end"
          paddingRight="16px"
        >
          <RateIndicatorIcon
            indicatorRate={engagement.indicator}
            indicatorChange={engagement.change}
            indicatorChangeAt={engagement.changed_at}
          />
          <Typography
            variant="body1"
            sx={{
              color: "#1F2932",
              ...(engagement.indicator && {
                color: indicatorMapper[engagement.indicator].color,
              }),
            }}
          >
            {engagement.score === null ? "--" : `${engagement.score}%`}
          </Typography>
        </Stack>
      </Grid>
      <Grid size={1.3}>
        <Link
          onClick={(e) => name === "--" && e.preventDefault()}
          to={`${AppRoutes.ACCOUNT}/${campaign_id}/${id}`}
          style={{ textDecoration: "none" }}
        >
          <Stack direction="row" gap="12px" alignItems="center">
            <EyeIcon />
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
              }}
            >
              Details
            </Typography>
          </Stack>
        </Link>
      </Grid>
    </Grid>
  );
};

type PenetrationRateTableProps = {
  data: Penetration[];
};

export const PenetrationRateTable = ({ data }: PenetrationRateTableProps) => {
  return (
    <Stack gap="14px" paddingY="24px">
      <Grid container padding="0 16px">
        <Grid size={0.5}></Grid>
        <Grid size={2.12}>
          <Typography
            variant="body1"
            noWrap={true}
            sx={{
              color: "#697184",
            }}
          >
            Name
          </Typography>
        </Grid>

        <Grid size={2.62}>
          <Typography
            variant="body1"
            noWrap={true}
            sx={{
              color: "#697184",
            }}
          >
            Industry
          </Typography>
        </Grid>
        <Grid size={2.01}>
          <Typography
            variant="body1"
            noWrap={true}
            sx={{
              color: "#697184",
            }}
          >
            Region
          </Typography>
        </Grid>
        <Grid size={1.6}>
          <Stack justifyContent="center" alignItems="flex-end">
            <EllipsisTooltip
              tooltip={
                "Rate of potential BC members, identified in the available data, considering the expected BC size."
              }
            >
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                  textAlign: "right",
                }}
              >
                Discovery
              </Typography>
            </EllipsisTooltip>
          </Stack>
        </Grid>
        <Grid size={1.85}>
          <Stack
            justifyContent="center"
            alignItems="flex-end"
            paddingRight="16px"
          >
            <EllipsisTooltip
              tooltip="Engagement rate of all identified potential BC members,
            based on outgoing and replied calls & emails, and communication recency over a selected period of time."
            >
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Engagement
              </Typography>
            </EllipsisTooltip>
          </Stack>
        </Grid>
        <Grid size={1.3}></Grid>
      </Grid>
      {(data.length > 0 ? data : mockPenetrationRate).map((item) => (
        <ItemList key={item.index} {...item} />
      ))}
    </Stack>
  );
};
